<template>
  <div>
    <!-- Main Content -->
    <div
      class="product-buybox"
      id="newProductBuyBox"
    >
      <h5 v-text="getProductText" />
      <div
        class="product-wrapper"
        :class="{ 'skeleton-loading': loading }"
      >
        <!-- Product Details Column -->
        <div class="product-details-column column-one">
          <ProductDetails
            :key="buyboxProduct?.sku || localProduct?.sku || 'product-details'"
            class="pdp-box"
            :product="productWithCategoryDesigns"
            :brand="brand"
            :download-files="productDownloads"
            :product-name="productDescription"
            :product-attributes="productAttributes"
            :product-with-category-designs="productWithCategoryDesigns"
            @open-variants="showVariants = true"
          />
          <DimensionsAndInstallation
            :key="buyboxProduct?.sku || localProduct?.sku || 'dimension-installation'"
            v-if="localProduct"
            :id="TELEPORT_NAMES.DIMENSION_ID"
            class="pdp-box product-bar__dimensions"
            :product="productWithCategoryDesigns"
            :product-attributes="productAttributes"
            :dimensions="productDimensionsFiltered"
            :product-downloads="productDownloads"
          />
        </div>

        <!-- Availability & Pricing Column -->
        <div
          class="availability-column column-two"
          v-if="localProduct && buyboxProduct"
        >
          <div
            :id="TELEPORT_NAMES.AVAILABILITY_PRICING_ID"
            class="product-bar__availability"
          >
            <AvailabilityPricing
              :key="buyboxProduct?.sku || localProduct?.sku || 'availability-pricing'"
              :product="buyboxProduct"
              :buy-box-product="buyboxProduct"
              :loading="loading"
              @addToCart="handleAddToCart"
              @close-pop-up="emit('close-pop-up')"
            />
          </div>
        </div>
      </div>
    </div>
    <PopupSuccessorVariants
      :show="showVariants"
      :sku-array="variantsAndSuccessor"
      @close-variants="showVariants = false"
      @close-all="handleCloseAll()"
      :second-layer="true"
    />
  </div>
</template>

<script setup>
import {ref, computed, watch, onMounted, onUnmounted, nextTick} from 'vue'
import {storeToRefs} from 'pinia'
import {useCartStore} from '@/store/cart'
import {useUserStore} from '@/store/user'
import {useProductDataStore} from '~/store/product-data'
import {useConfigurationDataStore} from '~/store/configuration-data'
import {useAuthStore} from '@/store/auth'

// Service imports
import {useProductService} from '@/composables/product_service'
import {useRightsHelper} from '~/composables/rights_helper'
import useI18nExtended from '~/composables/usei18nExtended'
import {usePimHelper} from '~/composables/pim_helper'

// Component imports
import ProductDetails from '~/components/product-detail/bb_components/ProductDetails'
import AvailabilityPricing from '~/components/product-detail/bb_components/AvailabilityPricing'
import DimensionsAndInstallation from '~/components/product-detail/bb_components/DimensionsAndInstallation'
import PopupSuccessorVariants from '~/components/popup/PopupSuccessorVariants'

// Utility imports
import {
  getMappedProductCategoryImagesNew,
  getMappedProductNew,
  getMappedProductAttributesNew
} from '~/assets/js/utils/product_attributes_mapping'
import {
  FILENAME_REGEX,
  // POPUP,
  PROPERTY_SCOPE_NAMES,
  ADDITEM_STATUS,
  USER_RIGHTS_MAPPING,
  TELEPORT_NAMES,
  BREAKPOINTS
} from '~/assets/js/constants'
import useGrid from '~/composables/useGrid'
// import {getNextDate} from '~/assets/js/utils'

// Props
const props = defineProps({
  // For compatibility with both usage patterns
  productData: {
    type: Object,
    required: false,
    default: null
  },
  product: {
    type: Object,
    required: false,
    default: null
  },
  isDefaultCustomer: {
    type: Boolean,
    default: false
  },
  loading: {
    type: Boolean,
    required: false,
    default: false
  },
  // Props for anonymous user functionality
  categoryWithDesign: {
    type: Object,
    default: null
  },
  categoryContents: {
    type: Object,
    default: null
  },
  attributes: {
    type: Array,
    default: () => []
  }
})

// Emits
const emit = defineEmits(['productSuccessfullyAdded', 'close-pop-up'])

// Composables
const nuxtApp = useNuxtApp()
const i18n = useI18nExtended()
const {userHasRight} = useRightsHelper()
const {loadProductCategoryWithPim} = usePimHelper()
const {getIndexPathValue} = useGrid()

// Stores
const {showInchValues} = storeToRefs(useUserStore())
const {addItems} = useCartStore()
const {getProductBySkuNew} = useProductService()
const {loggedIn} = storeToRefs(useAuthStore())

// Reactive data
const localProduct = ref(null)
const addInProgress = ref(false)
const configurations = ref(null)
const showVariants = ref(false)
const categoryJson = ref(null)

const route = useRoute()
if (props.product?.productDesign) {
  categoryJson.value = await loadProductCategoryWithPim(props.product?.productDesignTechnical, route)
}

// Computed properties
const isAuthenticated = computed(() => {
  return loggedIn && !props.isDefaultCustomer
})

const productSource = computed(() => {
  return props.productData || props.product
})

const productDescription = computed(() => {
  return localProduct.value?.locales[i18n.locale.value] ?? ''
})

const buyboxProduct = computed(() => {
  return getMappedProductNew(localProduct.value)
})

const brand = computed(() => {
  try {
    const productDataStore = useProductDataStore()
    const brands = productDataStore.brands
    let brand = brands.find(b => b.Code === localProduct.value?.brandName[0] && b.locale === i18n.locale.value)
    if (brand === undefined) {
      brand = brands.find(b => b.Code === localProduct.value?.brandName[0] && b.locale === i18n.defaultLocale)
    }
    return brand
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
    return null
  }
})

const productAttributes = computed(() => {
  if (categoryJson.value?.categoryAttributes_detail_attribute?.length === 0) {
    return []
  }

  const attributes = []
  categoryJson.value?.categoryAttributes_detail_attribute.forEach(ca => {
    const shortName = ca.shortName
    const shortLabel = shortName !== 'shortNameNotAvailable' ? ' (' + shortName + ')' : ''
    const identifier = ca.displayName + shortLabel
    const value = getIndexPathValue(ca.indexPath, localProduct.value)
    attributes.push({
      attributeIdentifier: identifier,
      value: i18n.tv(value, 'attributes.' + ca.technicalName + '-values')
    })
  })

  return attributes
})

const productDownloads = computed(() => {
  let result = []

  // Get non-datasheet assets from product index (CAD, InstallationSketch, etc.)
  if (props.product?.assets) {
    const nonDatasheetAssets = props.product.assets.filter(asset => asset.assetType !== 'ProductDataSheet')
    result = nonDatasheetAssets.filter(asset => asset.languageShortCode === i18n.locale.value)
    let presentAssetTypes = result.map(asset => asset.assetType)
    result = result.concat(nonDatasheetAssets.filter(asset => asset.languageShortCode === i18n.defaultLocale && !presentAssetTypes.includes(asset.assetType)))
    result = result.map((asset) => ({
      ...asset,
      // alt text is to be set to the filename to identify materials in the download section, the filename is extracted from the lookupKey (#28924)
      altText: asset.label ||
        (asset.lookupKey && FILENAME_REGEX.test(asset.lookupKey) && asset.lookupKey.match(FILENAME_REGEX)?.groups?.filename
          ? decodeURI(asset.lookupKey.match(FILENAME_REGEX)?.groups?.filename)
          : null),
      sortOrder: getSortOrder(asset.assetType)
    }))
  }

  // Add datasheets from PIM (categoryJson)
  if (categoryJson.value?.contents) {
    const pimBaseUrl = useRuntimeConfig().public.pimDataBaseUrl || ''
    const pimDatasheets = categoryJson.value.contents
      .filter(c => c.type === 'download')
      .map((categoryAsset) => {
        let downloadUrl = categoryAsset.url
        // Prepend PIM base URL if the URL is relative
        if (downloadUrl && !downloadUrl.startsWith('http://') && !downloadUrl.startsWith('https://')) {
          downloadUrl = pimBaseUrl + (downloadUrl.startsWith('/') ? '' : '/') + downloadUrl
        }

        return {
          assetType: 'ProductDataSheet',
          lookupKey: downloadUrl,
          altText: categoryAsset.label,
          label: categoryAsset.label,
          languageShortCode: categoryAsset.language || i18n.locale.value,
          sortOrder: getSortOrder('ProductDataSheet'),
          sourceCMS: true // Mark as coming from PIM/CMS
        }
      })
    result = result.concat(pimDatasheets)
  }

  return result?.length > 0 ? result.sort((a, b) => a.sortOrder - b.sortOrder) : result
})

const productDimensionsFiltered = computed(() => {
  if (categoryJson.value?.categoryAttributes_detail_dimension.length === 0) {
    return []
  }

  let returnDimensions = []
  let dimensionAttributes = getMappedProductAttributesNew(categoryJson.value?.categoryAttributes_detail_dimension, localProduct.value)
  dimensionAttributes.forEach(pa => {
    const shortName = pa.shortName
    const shortLabel = shortName !== 'shortNameNotAvailable' && shortName !== '' ? ' (' + shortName + ')' : ''
    const translatedProductAttribute = pa.displayName
    let value = getIndexPathValue(pa.indexPath, localProduct.value)
    if (pa.unitOfMeasure) {
      const lowerUnitOfMeasure = pa.unitOfMeasure.toLowerCase()
      const translatedUnitOfMeasure = showInchValues.value && lowerUnitOfMeasure === 'mm'
        ? i18n.tv('inch', 'unitsOfMeasure')
        : i18n.tv(lowerUnitOfMeasure, 'unitsOfMeasure')
      value += ' ' + translatedUnitOfMeasure
    }
    returnDimensions.push({
      technicalName: pa.technicalName,
      label: translatedProductAttribute !== shortName ? translatedProductAttribute + shortLabel : translatedProductAttribute,
      value: i18n.tv(value, 'attributes.' + pa.technicalName + '-values')
    })
  })
  return returnDimensions
})

const productWithCategoryDesigns = computed(() => {
  const result = getMappedProductCategoryImagesNew(getMappedProductNew(localProduct.value), categoryJson.value)
  return result
})

const getProductText = computed(() => {
  if (!localProduct.value) {
    return ''
  }
  const lang = i18n.locale.value.toUpperCase()
  const unit = showInchValues.value ? 'Imperial' : 'Metric'
  const otherUnit = showInchValues.value ? 'Metric' : 'Imperial'

  // Try the preferred unit first
  const primaryKey = 'locales_' + lang + '_' + unit + '_SalesTextECom'
  if (localProduct.value instanceof Object && primaryKey in localProduct.value && localProduct.value[primaryKey]) {
    return localProduct.value[primaryKey]
  }

  // Try the other unit as fallback
  const fallbackKey = 'locales_' + lang + '_' + otherUnit + '_SalesTextECom'
  if (localProduct.value instanceof Object && fallbackKey in localProduct.value && localProduct.value[fallbackKey]) {
    return localProduct.value[fallbackKey]
  }

  // Fallback to empty
  return ''
})

const variantsAndSuccessor = computed(() => {
  const variants = (localProduct.value?.otherVariants ?? []).map(v => ({...v, isSuccessor: false}))
  const successor = localProduct.value?.salesAreas.map(s => {
    // eslint-disable-next-line eqeqeq
    return s.successorSku != null ? {sku: s.successorSku, isSuccessor: true} : null
  })
  let result = null
  if (successor && variants) {
    result = [{sku: localProduct.value?.sku}, ...successor, ...variants]
  }
  else if (successor) {
    result = [{sku: localProduct.value?.sku}, ...successor]
  }
  else if (variants) {
    result = [{sku: localProduct.value?.sku}, ...variants]
  }
  else {
    result = [{sku: localProduct.value?.sku}]
  }
  // eslint-disable-next-line eqeqeq
  return result.filter(vs => vs != null)
})

// Methods
const getSortOrder = (assetType) => {
  // ProductDataSheet should appear first
  if (assetType === 'ProductDataSheet') {
    return 1
  }
  // CAD files second
  if (assetType === 'CAD') {
    return 2
  }
  // Everything else after
  return 0
}

const reloadProductWithAssets = async () => {
  const sku = localProduct.value?.sku || productSource.value?.sku
  if (sku) {
    try {
      const reloadedProduct = await getProductBySkuNew(
        sku,
        nuxtApp.$globalization.getRegion(),
        i18n.locale.value,
        showInchValues.value,
        null,
        PROPERTY_SCOPE_NAMES.PRODUCT_PAGE_NEW
      )

      if (reloadedProduct) {
        localProduct.value = reloadedProduct
      }
    } catch {
      // ignore error gracefully
    }
  }
}

const loadCategoryDataIfNeeded = async () => {
  // If categoryWithDesign or attributes are missing, load them
  if (!props.categoryWithDesign || !props.attributes || props.attributes.length === 0) {
    const productDesign = localProduct.value?.productDesignTechnical || productSource.value?.productDesignTechnical
    if (productDesign) {
      categoryJson.value = await loadProductCategoryWithPim(productDesign, route)
    }
  }
}

const handleAddToCart = async (payload) => {
  addInProgress.value = true
  const markedAsRequest = payload?.markedAsRequest ?? false
  const quantity = payload?.quantity ?? 1
  const availabilityDate = payload?.availabilityDate ?? null
  const warehouse = payload?.warehouse ?? null

  if (!userHasRight(USER_RIGHTS_MAPPING.PRODUCT_BUY) && !userHasRight(USER_RIGHTS_MAPPING.PRODUCT_REQUEST)) {
    nuxtApp.$toast.error(i18n.tv('failed', 'cart'))
    addInProgress.value = false
    return
  }

  try {
    let item = {
      sku: localProduct.value?.sku,
      quantity: quantity,
      availabilityDate: availabilityDate
    }

    if (markedAsRequest) {
      item.markedAsRequest = markedAsRequest
      item.isRequestItem = true
    }

    if (warehouse) {
      if (!warehouse.isDefault && warehouse.alternativePlantGuid) {
        item.alternativePlantUuid = warehouse.alternativePlantGuid
      }
      if (warehouse.plantCode) {
        item.plantCode = warehouse.plantCode
      } else if (localProduct.value?.defaultPlantCode) {
        item.plantCode = localProduct.value.defaultPlantCode
      }
    }

    let res = await addItems({
      itemsToAdd: [item],
      region: nuxtApp.$globalization.getRegion(),
      locale: i18n.locale.value
    })

    if (res && res?.length > 0 && res[0].status === ADDITEM_STATUS.SUCCESS) {
      emit('productSuccessfullyAdded')
      nuxtApp.$toast.success(i18n.tv('success', 'cart'))
    } else {
      nuxtApp.$toast.error(i18n.tv('failed', 'cart'))
    }
  } catch (e) {
    nuxtApp.$toast.error(i18n.tv('failed', 'cart'))
  }

  addInProgress.value = false
}

const checkMobile = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }

  const availabilityPricingComponent = document.getElementById(TELEPORT_NAMES.AVAILABILITY_PRICING_ID)
  if (!availabilityPricingComponent) {
    nextTick(() => {
      const node = document.getElementById(TELEPORT_NAMES.AVAILABILITY_PRICING_ID)
      if (!node) {
        return
      }
      checkMobile()
    })
    return
  }

  const lowerBreakpoint = window.innerWidth < BREAKPOINTS.BETWEEN_MD_LG
  const column = lowerBreakpoint
    ? document.getElementsByClassName(TELEPORT_NAMES.COLUMN_ONE).item(0)
    : document.getElementsByClassName(TELEPORT_NAMES.COLUMN_TWO).item(0)

  if (!column) {
    return
  }

  if (lowerBreakpoint) {
    const dimensionComponent = document.getElementById(TELEPORT_NAMES.DIMENSION_ID) || null
    if (availabilityPricingComponent.parentElement !== column) {
      column.insertBefore(availabilityPricingComponent, dimensionComponent)
    }
  } else {
    if (availabilityPricingComponent.parentElement !== column) {
      column.appendChild(availabilityPricingComponent)
    }
  }
}

// Emits handler for closing all popups from variants popup
const handleCloseAll = () => {
  showVariants.value = false
  emit('close-pop-up')
}

// Lifecycle hooks
onMounted(async () => {
  // Load configurations for partner hub
  const configurationDataStore = useConfigurationDataStore()
  await configurationDataStore.ensureConfigurationsLoaded()
  configurations.value = configurationDataStore.configurations

  // Set initial product
  localProduct.value = productSource.value

  // For anonymous users, reload product with proper scope to ensure all assets are loaded
  if (!isAuthenticated.value) {
    await reloadProductWithAssets()
  }

  // Load category data if not provided as props
  await loadCategoryDataIfNeeded()

  // Setup mobile responsive layout
  await nextTick()
  checkMobile()
  window.addEventListener('resize', checkMobile)

  // Listen for warehouse changes
  nuxtApp.$eventbus.on('emit-warehouse-change', async (alternativePlantCode) => {
    if (!localProduct.value?.sku) {
      return
    }
    const newProductData = await getProductBySkuNew(
      localProduct.value.sku,
      nuxtApp.$globalization.getRegion(),
      i18n.locale.value,
      showInchValues.value,
      alternativePlantCode,
      PROPERTY_SCOPE_NAMES.PRODUCT_PAGE_NEW
    )
    localProduct.value = newProductData
    await loadCategoryDataIfNeeded()
  })
})

onUnmounted(() => {
  nuxtApp.$eventbus.off('emit-warehouse-change')
  window.removeEventListener('resize', checkMobile)
})

// Watchers
watch(() => productSource.value, (newValue) => {
  localProduct.value = newValue
  if (newValue) {
    loadCategoryDataIfNeeded()
  }
}, {immediate: false})

watch(localProduct, async (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    await loadCategoryDataIfNeeded()
  }
}, {deep: true})

watch(showInchValues, async () => {
  if (localProduct.value?.sku) {
    try {
      const reloadedProduct = await getProductBySkuNew(
        localProduct.value.sku,
        nuxtApp.$globalization.getRegion(),
        i18n.locale.value,
        showInchValues.value,
        localProduct.value?.alternativePlantCode ?? null,
        PROPERTY_SCOPE_NAMES.PRODUCT_PAGE_NEW
      )
      if (reloadedProduct) {
        localProduct.value = reloadedProduct
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Failed to reload product on inch values change:', e)
    }
  }
}, {immediate: false})
</script>

<style lang="scss">
/* Base styles for the product buybox */
.pdp-box {
  box-shadow: $shadow-default;
}

.product-buybox {
  background: $color__white;
  padding: 0;

  h5 {
    margin: 0 0 1rem;
  }

  .product-wrapper {
    display: flex;
    gap: 1rem;
    max-width: 90rem;

    > .product-details-column,
    > .column-two {
      flex: 0 0 calc(50% - 0.5rem);
      max-width: calc(50% - 0.5rem);
      min-width: 0;
      box-sizing: border-box;
    }

    @include breakpoint-down($lg) {
      flex-direction: column;

      > .product-details-column,
      > .column-two {
        flex: none;
        width: 100%;
        max-width: 100%;
      }
    }

    .product-details-column {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      min-width: 0;
    }

    // Hide availability column on mobile since content is teleported to column-one
    @include breakpoint-down($lg) {
      .column-two {
        display: none;
      }
    }

    .column-two {
      display: flex;
      flex-direction: column;
      width: 100%;
      min-width: 0;

      > * {
        width: 100%;
        flex: 1 1 auto;
        min-width: 0;
      }

      .product-bar__availability,
      .availability-pricing,
      .availability-and-pricing-skeleton {
        width: 100%;
        max-width: 100%;
        flex: 1 1 auto;
        min-width: 0;
      }

      .pdp-box,
      .availability-pricing,
      .availability-and-pricing-skeleton {
        box-sizing: border-box;
      }
    }
  }
}

.popup {
  &.popup__buy-box-new {
    .popup__wrapper {
      overflow: hidden;
      max-width: 78rem;

      @include breakpoint-down($between_md_lg) {
        max-width: 39rem;

        .product-wrapper {
          flex-direction: column;

          .product-details-column,
          .column-two {
            flex: none;
            width: 100%;
            max-width: 100%;
          }
        }
      }

      @include breakpoint-down($md) {
        .product-details-column .pdp-box:not(:last-child) {
          margin-bottom: 1rem;
        }

        .column-two {
          margin-top: 1rem;
        }
      }

      .pdp-box.availability-and-pricing-skeleton,
      .availability-and-pricing-skeleton {
        width: 100% !important;
        max-width: 100% !important;
      }
    }
  }
}

.product-wrapper.skeleton-loading {
  > .product-details-column,
  > .column-two {
    flex: 0 0 calc(50% - 0.5rem) !important;
    width: calc(50% - 0.5rem) !important;
    max-width: calc(50% - 0.5rem) !important;
    min-width: 0;
  }
}
</style>
